import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import './TaskArticlesSection.css';

const linesFingerprint = (list) => (list || [])
  .map((line) => `${line.id}:${line.quantity ?? ''}:${line.timeMinutes ?? ''}`)
  .join('|');

const TaskArticlesSection = ({
  taskId,
  companyId = null,
  canMutate = false,
  isCompleted = false,
  onLinesChange = null,
  compact = false,
  seedLines = null,
  seedArticles = null,
  /** Parent-owned lines mirror (e.g. Klar add) — sync without skipping fetch. */
  syncedLines = null,
  /** Klar done-payload drafts via onQuantityChange; quantity editable even if !canMutate. */
  quantities = null,
  onQuantityChange = null
}) => {
  const { t } = useTranslation(['tasks', 'articles', 'common']);
  const fieldIdPrefix = useId();
  const seeded = seedLines != null && seedArticles != null;
  const [loading, setLoading] = useState(!seeded);
  const [lines, setLines] = useState(seedLines || []);
  const [articles, setArticles] = useState(seedArticles || []);
  const [sectionError, setSectionError] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [articleId, setArticleId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [timeMinutes, setTimeMinutes] = useState('');
  const [editQty, setEditQty] = useState({});
  const [editTime, setEditTime] = useState({});
  const syncedFpRef = useRef(linesFingerprint(seedLines || []));

  const selectedArticle = articles.find((a) => String(a.id) === String(articleId));
  const unitMode = selectedArticle?.unit_mode || selectedArticle?.unitMode;
  const needsQuantity = unitMode === 'quantity' || unitMode === 'both';
  const needsTime = unitMode === 'time' || unitMode === 'both';

  const setLinesAndNotify = (nextLines) => {
    syncedFpRef.current = linesFingerprint(nextLines);
    setLines(nextLines);
    if (onLinesChange) {
      onLinesChange(nextLines);
    }
  };

  const syncEditFields = (nextLines) => {
    const qtyMap = {};
    const timeMap = {};
    (nextLines || []).forEach((line) => {
      if (line.quantity !== null && line.quantity !== undefined) {
        qtyMap[line.id] = String(Math.trunc(Number(line.quantity)));
      }
      if (line.timeMinutes !== null && line.timeMinutes !== undefined) {
        timeMap[line.id] = String(Math.trunc(Number(line.timeMinutes)));
      }
    });
    setEditQty(qtyMap);
    setEditTime(timeMap);
  };

  useEffect(() => {
    if (!seeded) {
      return;
    }
    const next = seedLines || [];
    syncedFpRef.current = linesFingerprint(next);
    setLines(next);
    setArticles(seedArticles || []);
    syncEditFields(next);
    setLoading(false);
  }, [seeded, seedLines, seedArticles]);

  useEffect(() => {
    if (seeded || syncedLines == null) {
      return;
    }
    const fp = linesFingerprint(syncedLines);
    if (fp === syncedFpRef.current) {
      return;
    }
    syncedFpRef.current = fp;
    setLines(syncedLines);
    syncEditFields(syncedLines);
  }, [seeded, syncedLines]);

  useEffect(() => {
    if (seeded) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSectionError('');
      try {
        const [linesRes, articlesRes] = await Promise.all([
          axios.get(`/api/tasks/${taskId}/articles`),
          axios.get('/api/articles', companyId ? { params: { companyId } } : undefined)
        ]);
        if (cancelled) {
          return;
        }
        const nextLines = linesRes.data?.data?.lines || [];
        syncedFpRef.current = linesFingerprint(nextLines);
        setLines(nextLines);
        if (onLinesChange) {
          onLinesChange(nextLines);
        }
        syncEditFields(nextLines);
        const allArticles = articlesRes.data?.data || [];
        setArticles(
          companyId
            ? allArticles.filter((article) => Number(article.company_id) === Number(companyId))
            : allArticles
        );
      } catch (err) {
        if (!cancelled) {
          setSectionError(formatError(err) || t('tasks:taskArticlesLoadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (taskId) {
      load();
    }

    return () => {
      cancelled = true;
    };
    // Intentionally omit onLinesChange — parent setter identity may churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, companyId, t, seeded]);

  const handleAdd = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!articleId) {
      setSectionError(t('tasks:taskArticlesArticleRequired'));
      return;
    }
    setAdding(true);
    setSectionError('');
    try {
      const payload = { articleId: Number(articleId) };
      if (needsQuantity) {
        if (!/^[1-9]\d*$/.test(String(quantity).trim())) {
          setSectionError(t('tasks:taskArticlesQuantityInvalid'));
          setAdding(false);
          return;
        }
        payload.quantity = Number.parseInt(String(quantity).trim(), 10);
      }
      if (needsTime) {
        if (!/^[1-9]\d*$/.test(String(timeMinutes).trim())) {
          setSectionError(t('tasks:taskArticlesTimeInvalid'));
          setAdding(false);
          return;
        }
        payload.timeMinutes = Number.parseInt(String(timeMinutes).trim(), 10);
      }
      const response = await axios.post(`/api/tasks/${taskId}/articles`, payload);
      const nextLines = response.data?.data?.lines || [];
      setLinesAndNotify(nextLines);
      syncEditFields(nextLines);
      setArticleId('');
      setQuantity('1');
      setTimeMinutes('');
    } catch (err) {
      setSectionError(formatError(err) || t('tasks:taskArticlesAddFailed'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (lineId) => {
    setRemovingId(lineId);
    setSectionError('');
    try {
      const response = await axios.delete(`/api/tasks/${taskId}/articles/${lineId}`);
      const nextLines = response.data?.data?.lines || [];
      setLinesAndNotify(nextLines);
      syncEditFields(nextLines);
    } catch (err) {
      setSectionError(formatError(err) || t('tasks:taskArticlesRemoveFailed'));
    } finally {
      setRemovingId(null);
    }
  };

  const persistLineAmounts = async (line, { quantity: nextQty, timeMinutes: nextMins }) => {
    const needsQty = line.unitMode === 'quantity' || line.unitMode === 'both';
    const needsMins = line.unitMode === 'time' || line.unitMode === 'both';
    const payload = {};

    if (needsQty) {
      if (nextQty == null) {
        return;
      }
      payload.quantity = nextQty;
    }
    if (needsMins) {
      if (nextMins == null) {
        return;
      }
      payload.timeMinutes = nextMins;
    }

    setSavingId(line.id);
    setSectionError('');
    try {
      const response = await axios.patch(`/api/tasks/${taskId}/articles/${line.id}`, payload);
      const nextLines = response.data?.data?.lines || [];
      setLinesAndNotify(nextLines);
      syncEditFields(nextLines);
    } catch (err) {
      setSectionError(formatError(err) || t('tasks:taskArticlesUpdateFailed'));
    } finally {
      setSavingId(null);
    }
  };

  const persistField = async (line, field) => {
    const needsQty = line.unitMode === 'quantity' || line.unitMode === 'both';
    const needsMins = line.unitMode === 'time' || line.unitMode === 'both';
    if (field === 'quantity' && !needsQty) {
      return;
    }
    if (field === 'time' && !needsMins) {
      return;
    }

    const raw = String(
      (field === 'quantity'
        ? (quantities && quantities[line.id] !== undefined ? quantities[line.id] : editQty[line.id])
        : editTime[line.id]) ?? ''
    ).trim();
    const invalidMsg = field === 'quantity'
      ? t('tasks:taskArticlesQuantityInvalid')
      : t('tasks:taskArticlesTimeInvalid');
    if (!/^[1-9]\d*$/.test(raw)) {
      setSectionError(invalidMsg);
      if (field === 'quantity') {
        const reverted = line.quantity != null ? String(Math.trunc(Number(line.quantity))) : '1';
        setEditQty((prev) => ({
          ...prev,
          [line.id]: reverted
        }));
        if (onQuantityChange) {
          onQuantityChange(line.id, reverted);
        }
      } else {
        setEditTime((prev) => ({
          ...prev,
          [line.id]: line.timeMinutes != null ? String(Math.trunc(Number(line.timeMinutes))) : ''
        }));
      }
      return;
    }

    const nextValue = Number.parseInt(raw, 10);
    const currentValue = field === 'quantity'
      ? (line.quantity != null ? Math.trunc(Number(line.quantity)) : null)
      : (line.timeMinutes != null ? Math.trunc(Number(line.timeMinutes)) : null);
    if (nextValue === currentValue) {
      return;
    }

    await persistLineAmounts(line, {
      quantity: field === 'quantity'
        ? nextValue
        : (needsQty && line.quantity != null ? Math.trunc(Number(line.quantity)) : null),
      timeMinutes: field === 'time'
        ? nextValue
        : (needsMins && line.timeMinutes != null ? Math.trunc(Number(line.timeMinutes)) : null)
    });
  };

  const formatLineMeta = (line) => {
    const parts = [];
    if (line.quantity !== null && line.quantity !== undefined) {
      parts.push(t('tasks:taskArticlesQty', { quantity: line.quantity }));
    }
    if (line.timeMinutes !== null && line.timeMinutes !== undefined) {
      parts.push(t('tasks:taskArticlesTime', { minutes: line.timeMinutes }));
    }
    return parts.join(' · ');
  };

  if (loading) {
    return null;
  }

  const linkedIds = new Set(lines.map((line) => String(line.articleId)));
  const availableArticles = articles.filter((a) => !linkedIds.has(String(a.id)));
  const addRowClass = needsQuantity && needsTime
    ? 'task-articles-add-row task-articles-add-row--both'
    : needsQuantity || needsTime
      ? 'task-articles-add-row task-articles-add-row--one'
      : 'task-articles-add-row';

  const renderLineEditors = (line) => {
    const lineNeedsQty = line.unitMode === 'quantity' || line.unitMode === 'both';
    const lineNeedsTime = line.unitMode === 'time' || line.unitMode === 'both';
    // Klar: qty editable when canMutate or parent owns done-payload drafts (onQuantityChange).
    const showQtyEdit = lineNeedsQty && (canMutate || Boolean(onQuantityChange));
    const showTimeEdit = canMutate && lineNeedsTime;
    const qtyValue = quantities && quantities[line.id] !== undefined
      ? quantities[line.id]
      : (editQty[line.id] ?? (line.quantity != null ? String(Math.trunc(Number(line.quantity))) : '1'));

    if (!showQtyEdit && !showTimeEdit) {
      return formatLineMeta(line) ? (
        <span className="task-articles-meta">{formatLineMeta(line)}</span>
      ) : null;
    }

    const qtyInputId = `${fieldIdPrefix}-qty-${line.id}`;
    const timeInputId = `${fieldIdPrefix}-time-${line.id}`;

    return (
      <div className="task-articles-line-edit">
        {showQtyEdit && (
          <div className="task-articles-field">
            <label className="task-articles-field-label" htmlFor={qtyInputId}>
              {t('tasks:taskArticlesQuantityLabel')}
            </label>
            <input
              id={qtyInputId}
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              className="task-time-input"
              value={qtyValue}
              onChange={(e) => {
                const value = e.target.value;
                setEditQty((prev) => ({
                  ...prev,
                  [line.id]: value
                }));
                if (onQuantityChange) {
                  onQuantityChange(line.id, value);
                }
              }}
              onBlur={() => {
                if (canMutate) {
                  persistField(line, 'quantity');
                }
              }}
              disabled={savingId === line.id}
              aria-label={`${t('tasks:taskArticlesQuantityLabel')} — ${line.articleName}`}
            />
          </div>
        )}
        {showTimeEdit && (
          <div className="task-articles-field">
            <label className="task-articles-field-label" htmlFor={timeInputId}>
              {t('tasks:taskArticlesTimeLabel')}
            </label>
            <input
              id={timeInputId}
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              className="task-time-input"
              value={editTime[line.id] ?? ''}
              onChange={(e) => setEditTime((prev) => ({
                ...prev,
                [line.id]: e.target.value
              }))}
              onBlur={() => persistField(line, 'time')}
              disabled={savingId === line.id}
              aria-label={`${t('tasks:taskArticlesTimeLabel')} — ${line.articleName}`}
            />
          </div>
        )}
        {!showTimeEdit && line.timeMinutes != null && (
          <span className="task-articles-meta">
            {t('tasks:taskArticlesTime', { minutes: line.timeMinutes })}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`task-articles-section${compact ? ' task-articles-section--compact' : ''}`}>
      {!compact && (
        <>
          <h3 className="task-section-label">{t('tasks:taskArticlesTitle')}</h3>
          <p className="section-description">{t('tasks:taskArticlesDescription')}</p>
        </>
      )}
      {compact && (
        <h4 className="task-section-label">{t('tasks:taskArticlesTitle')}</h4>
      )}

      {sectionError && <div className="error">{sectionError}</div>}

      {lines.length > 0 && (
        <ul className="task-articles-lines">
          {lines.map((line) => (
            <li key={line.id} className="task-articles-line">
              <div className="task-articles-line-info">
                <span className="task-articles-name">{line.articleName}</span>
                {renderLineEditors(line)}
              </div>
              {canMutate && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleRemove(line.id)}
                  disabled={removingId === line.id}
                >
                  {t('common:delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {lines.length === 0 && (
        <p className="task-articles-empty">{t('tasks:taskArticlesEmpty')}</p>
      )}

      {canMutate && (
        /* Own form OK: Klar hosts this outside the checkout form. */
        <form className="task-articles-add-form" onSubmit={handleAdd}>
          <div className={addRowClass}>
            <select
              value={articleId}
              onChange={(e) => setArticleId(e.target.value)}
              disabled={adding || availableArticles.length === 0}
              aria-label={t('tasks:taskArticlesArticleLabel')}
            >
              <option value="">{t('tasks:taskArticlesArticlePlaceholder')}</option>
              {availableArticles.map((article) => (
                <option key={article.id} value={article.id}>
                  {article.name}
                </option>
              ))}
            </select>
            {needsQuantity && (
              <div className="task-articles-field">
                <label className="task-articles-field-label" htmlFor={`${fieldIdPrefix}-add-qty`}>
                  {t('tasks:taskArticlesQuantityLabel')}
                </label>
                <input
                  id={`${fieldIdPrefix}-add-qty`}
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  className="task-time-input"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  disabled={adding}
                />
              </div>
            )}
            {needsTime && (
              <div className="task-articles-field">
                <label className="task-articles-field-label" htmlFor={`${fieldIdPrefix}-add-time`}>
                  {t('tasks:taskArticlesTimeLabel')}
                </label>
                <input
                  id={`${fieldIdPrefix}-add-time`}
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  className="task-time-input"
                  value={timeMinutes}
                  onChange={(e) => setTimeMinutes(e.target.value)}
                  disabled={adding}
                />
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={adding || !articleId}
            >
              {t('tasks:taskArticlesAddLine')}
            </button>
          </div>
        </form>
      )}

      {!canMutate && !compact && (
        <p className="task-articles-hint">
          {isCompleted
            ? t('tasks:taskArticlesCompletedHint')
            : t('tasks:taskArticlesCheckedInHint')}
        </p>
      )}
    </div>
  );
};

export default TaskArticlesSection;
